<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACTIVE_ACCOUNTS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="activeAccountTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="activeAccountRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
            <!-- Account Number -->
          </b-row>
          <b-row class="match-height">
            <!-- Transaction Date From -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Transaction Date (From)')"
                rules="required"
                :error-name="$t('Transaction Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Transaction Date From -->
            <!-- Transaction Date To -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                :label="$t('Transaction Date (To)')"
                :error-name="$t('Transaction Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
          <!-- Transaction Date To -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import { heightFade } from '@core/directives/animations'
import { toYearWithDate } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      isDisabled: true,
      required,
      modalTitle: this.$t('Active Account Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'productName', sortable: true, label: 'Product' },
        { key: 'billingMonth', sortable: true, label: 'Billing Month' },
        { key: 'transactionCount', sortable: true, label: 'Transaction Count' },
      ],
      filter: {
        accountNumber: '',
        financialDateFrom: null,
        financialDateTo: null,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchActiveAccountReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchActiveAccountReport()
    },
  },
  methods: {
    ...mapActions({
      getActiveAccountReport: 'reports/getActiveAccountReport',
      downloadActiveAccountReport: 'reports/downloadActiveAccountReport',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.fetchActiveAccountReport()
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.activeAccountRules.validate().then(async success => {
        if (success) {
          this.fetchActiveAccountReport()
        }
      })
    },
    async fetchActiveAccountReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const filteredData = {
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== null ? toYearWithDate(this.filter.financialDateFrom) : '',
          financialDateTo: this.filter.financialDateTo !== null ? toYearWithDate(this.filter.financialDateTo) : '',
        }
        const response = await this.getActiveAccountReport({
          search: filteredData,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadActiveAccountReport({
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== null ? toYearWithDate(this.filter.financialDateFrom) : '',
          financialDateTo: this.filter.financialDateTo !== null ? toYearWithDate(this.filter.financialDateTo) : '',
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Active Account Report.csv')
          this.alertSuccess({ text: this.$t('Your request to download Active Account Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadActiveAccountReport({
          ...this.filter,
          financialDateFrom: this.filter.financialDateFrom !== null ? toYearWithDate(this.filter.financialDateFrom) : '',
          financialDateTo: this.filter.financialDateTo !== null ? toYearWithDate(this.filter.financialDateTo) : '',
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'Active Account Report.xlsx')
          this.alertSuccess({ text: this.$t('Your request to download Active Account Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.invoice-filter-select {
  min-width: 190px;
  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }
  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
